// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md
// @greenlight-spec: 10-security-compliance.md
// Org-level IdP group → platform role mapping matrix. Owns its own
// api.settings.get query (shared cache with ['settings'] key) and the
// updateIdentity mutation that reconfigures the identity provider.
import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';

const RBAC_ROLES = [
  { key: 'org_admin' as const, label: 'Org admin' },
  { key: 'admin' as const, label: 'Admin' },
  { key: 'user' as const, label: 'User' },
];

export function RbacMatrix(): React.JSX.Element {
  const queryClient = useQueryClient();
  const {
    data: settingsData,
    isLoading,
    isError,
  } = api.settings.get.useQuery(['settings-rbac'], undefined, { queryKey: ['settings'] });
  const updateIdentity = api.settings.updateIdentity.useMutation();
  const [saving, setSaving] = React.useState(false);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [pendingAction, setPendingAction] = React.useState<'add' | 'remove' | null>(null);
  const [pendingUpdated, setPendingUpdated] = React.useState<typeof groups>([]);

  const [addOpen, setAddOpen] = React.useState(false);
  const [addRole, setAddRole] = React.useState<'org_admin' | 'admin' | 'user' | null>(null);
  const [newGroupId, setNewGroupId] = React.useState('');

  const settings = settingsData?.status === 200 ? settingsData.body : undefined;
  const identity = settings?.identity;
  const roleGroups = identity?.role_groups ?? {};
  const hasIdentity = identity !== undefined;

  const displayNames = identity?.group_display_names ?? {};

  const groups: Array<{ id: string; role: 'org_admin' | 'admin' | 'user' }> = [];
  for (const role of RBAC_ROLES) {
    const ids = roleGroups[role.key];
    if (ids) {
      for (const id of ids) {
        groups.push({ id, role: role.key });
      }
    }
  }

  const executeSave = async (updated: typeof groups): Promise<boolean> => {
    if (!identity) return false;
    setSaving(true);
    const newRoleGroups: Record<string, string[]> = {};
    for (const g of updated) {
      (newRoleGroups[g.role] ??= []).push(g.id);
    }
    try {
      const body: Record<string, unknown> = {
        provider: identity.provider,
        issuer_url: identity.oidc_issuer,
        client_id: identity.client_id,
        group_claim: identity.group_claim,
        role_groups: {
          org_admin: newRoleGroups.org_admin ?? [],
          admin: newRoleGroups.admin ?? [],
          user: newRoleGroups.user ?? [],
        },
      };
      // Carried through unchanged; this surface edits only the role mapping and
      // an omitted key would clear the org's Workspace-domain restriction.
      if (identity.provider === 'google') body.hosted_domains = identity.hosted_domains ?? [];
      const res = await updateIdentity.mutateAsync({
        body: body as Parameters<typeof updateIdentity.mutateAsync>[0]['body'],
      });
      if (res.status === 200) {
        toast.success('Group-to-role mapping updated.');
        void queryClient.invalidateQueries({ queryKey: ['settings'] });
        return true;
      }
      toast.error("Couldn't update the role mapping.");
      return false;
    } catch {
      toast.error("Couldn't update the role mapping.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const requestSave = (updated: typeof groups, action: 'add' | 'remove'): void => {
    setPendingUpdated(updated);
    setPendingAction(action);
    setConfirmOpen(true);
  };

  const handleRemove = (groupToRemove: (typeof groups)[number]): void => {
    const updated = groups.filter(
      (g) => !(g.id === groupToRemove.id && g.role === groupToRemove.role),
    );
    requestSave(updated, 'remove');
  };

  const handleAdd = (): void => {
    if (!addRole || !newGroupId.trim()) return;
    const trimmedId = newGroupId.trim();
    // Reject duplicate (id, role) pairs — the same group can't be mapped to the
    // same role twice.
    if (groups.some((g) => g.id === trimmedId && g.role === addRole)) return;
    const updated = [...groups, { id: trimmedId, role: addRole }];
    setAddOpen(false);
    requestSave(updated, 'add');
  };

  const handleConfirmSave = async (): Promise<void> => {
    if (saving) return;
    setConfirmOpen(false);
    const ok = await executeSave(pendingUpdated);
    if (ok && pendingAction === 'add') {
      setAddRole(null);
      setNewGroupId('');
    }
  };

  if (isLoading) {
    return (
      <section className="space-y-3" data-testid="rbac-matrix-loading">
        <Skeleton className="h-5 w-48" />
        <Skeleton className="h-24 w-full rounded-gl-lg" />
      </section>
    );
  }

  if (isError || !hasIdentity || identity?.provider === 'google') {
    // Identity not configured yet, or a Google org — Google has no group claim,
    // so roles are managed per-user in the table above, not via IdP-group
    // mapping (the contract rejects role_groups for Google). Nothing to map here.
    return <></>;
  }

  return (
    <section className="space-y-3" data-testid="rbac-matrix">
      {/* Eyebrow */}
      <div className="flex flex-wrap items-baseline gap-2 ml-0.5">
        <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] font-medium text-foreground">
          Role mapping
        </span>
        <span className="font-mono text-[10.5px] text-muted-foreground">{groups.length}</span>
        <span className="text-[11.5px] text-muted-foreground hidden sm:inline">
          IdP groups mapped to platform roles
        </span>
        <Button
          variant="outline"
          size="sm"
          className="ml-auto h-11 md:h-9"
          onClick={() => setAddOpen(true)}
          disabled={saving}
        >
          <Plus className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
          Map group
        </Button>
      </div>

      {/* Help text */}
      {/* role_groups.user doubles as the sign-in restriction (docs/28 §/users). */}
      <p className="text-[12px] text-muted-foreground leading-relaxed">
        {(roleGroups.user?.length ?? 0) > 0
          ? 'Only members of the mapped groups can sign in.'
          : groups.length === 0
            ? 'No groups mapped — everyone in your IdP can sign in and lands as User. Add the first mapping to restrict sign-in.'
            : 'No User group mapped — everyone in your IdP can sign in. Map a group to User to restrict sign-in.'}
      </p>

      {/* Horizontal-scrollable table */}
      <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
        <div className="min-w-[420px] rounded-gl-lg border border-border bg-card overflow-hidden">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="text-left font-mono text-[10.5px] uppercase tracking-[0.07em] text-muted-foreground px-4 py-2.5">
                  IdP group
                </th>
                <th className="text-left font-mono text-[10.5px] uppercase tracking-[0.07em] text-muted-foreground px-4 py-2.5 w-[120px]">
                  Role
                </th>
                <th className="w-10 px-2 py-2.5" aria-label="Actions" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {groups.map((g) => (
                <tr key={`${g.role}-${g.id}`} className="hover:bg-accent/30">
                  <td className="px-4 py-2.5 max-w-[240px]">
                    {displayNames[g.id] ? (
                      <div className="flex flex-col gap-0.5">
                        <span className="text-[12.5px] text-foreground truncate">
                          {displayNames[g.id]}
                        </span>
                        <span className="font-mono text-[11px] text-muted-foreground truncate">
                          {g.id}
                        </span>
                      </div>
                    ) : (
                      <span className="font-mono text-[12px] block truncate">{g.id}</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-muted font-mono text-[10.5px] text-foreground">
                      {RBAC_ROLES.find((r) => r.key === g.role)?.label ?? g.role}
                    </span>
                  </td>
                  <td className="px-2 py-2.5">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-11 w-11 md:h-7 md:w-7"
                      aria-label={`Remove ${g.id} from ${RBAC_ROLES.find((r) => r.key === g.role)?.label}`}
                      onClick={() => void handleRemove(g)}
                      disabled={saving}
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden />
                    </Button>
                  </td>
                </tr>
              ))}
              {groups.length === 0 && (
                <tr>
                  <td
                    colSpan={3}
                    className="px-4 py-4 text-center text-[12px] text-muted-foreground"
                  >
                    No role mappings configured.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {saving && <p className="text-[12px] text-muted-foreground">Saving…</p>}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="rounded-[18px] ad-shadow">
          <DialogHeader>
            <DialogTitle>Map an IdP group</DialogTitle>
            <DialogDescription>
              Members of this group get the role you pick when they sign in.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="rbac-group-id">Group identifier</Label>
              <Input
                id="rbac-group-id"
                value={newGroupId}
                onChange={(e) => setNewGroupId(e.target.value)}
                aria-describedby="rbac-group-id-help"
                className="h-11 font-mono text-[12px] md:h-9"
              />
              <p id="rbac-group-id-help" className="text-[12px] text-muted-foreground">
                As your IdP emits it on the{' '}
                <span className="font-mono">{identity.group_claim}</span> claim:
                {identity.provider === 'entra'
                  ? ' Entra object IDs (e.g. 00000000-0000-0000-0000-000000000000).'
                  : identity.provider === 'okta'
                    ? ' Okta group claim values (e.g. everyone or 00g123abc).'
                    : ' the exact values your provider emits in that claim (often group names).'}
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rbac-role">Role</Label>
              <Select value={addRole ?? ''} onValueChange={(v) => setAddRole(v as typeof addRole)}>
                <SelectTrigger id="rbac-role" className="h-11 md:h-9" aria-label="Role">
                  <SelectValue placeholder="Pick a role" />
                </SelectTrigger>
                <SelectContent>
                  {RBAC_ROLES.map((r) => (
                    <SelectItem key={r.key} value={r.key}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAdd} disabled={!addRole || !newGroupId.trim() || saving}>
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm dialog — warns about session revocation */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="rounded-[18px] ad-shadow">
          <DialogHeader>
            <DialogTitle>Reconfigure identity provider?</DialogTitle>
            <DialogDescription>
              Updating the group-to-role mapping reconfigures the identity provider and will sign
              out every active session — including yours. You will need to sign in again.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button variant="default" onClick={() => void handleConfirmSave()} disabled={saving}>
              Update mapping
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
